/**
 * ProjectRow Component
 *
 * Displays a project in row format using BaseRow layout.
 * Shows project status, client, progress, and quick actions.
 *
 * Layout follows the standard row pattern:
 * [PrimaryAction] [Avatar] [Title + Meta] ... [Fast Actions]
 */

import React, { useMemo, useState } from 'react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction, EntityAvatar } from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { useTranslation } from '../../hooks';
import type { Project } from '@business-assistant/shared/domain';
import type { ActionType } from '../../types/row.types';

// =============================================================================
// TYPES
// =============================================================================

export interface ProjectRowProps {
  entity: Project;
  onTitleClick?: (project: Project) => void;
  onActionClick?: (actionType: string, projectId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ProjectRow({ entity: project, onTitleClick, onActionClick, onAvatarClick }: ProjectRowProps) {
  const { t } = useTranslation();

  // Map Italian status values to translation keys
  const getStatusKey = (status: string): string => {
    const statusMap: Record<string, string> = {
      'da-iniziare': 'planning',
      'in-corso': 'active',
      'a-rischio': 'atRisk',
      'in-pausa': 'paused',
      'chiuso': 'completed',
    };
    return statusMap[status] || status;
  };

  // Primary action state (favorite)
  const [isFavorite, setIsFavorite] = useState(project.favorite || false);

  // Get linked action counts for icon coloring
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(project.id);
  }, [project.id]);

  // Left slot - Primary Action (star/favorite) + Project Avatar
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="star"
        active={isFavorite}
        onChange={(favorite) => {
          setIsFavorite(favorite);
          onActionClick?.('favorite', project.id);
        }}
      />
      <EntityAvatar
        items={{
          id: project.id,
          name: project.clientName || project.title,
          type: 'company',
        }}
        type="company"
        size={24}
        color={getProjectAvatarColor(project.status)}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // Default available actions for projects
  const availableActions = useMemo((): ActionType[] => {
    // Projects can have: tasks, events, files, invoices, notes, emails, calls
    return ['task', 'event', 'file', 'invoice', 'note', 'email', 'call'];
  }, []);

  // Build meta content
  const metaContent = useMemo(() => {
    return (
      <div style={styles.metaContainer}>
        {/* Client name */}
        {project.clientName && (
          <span style={styles.metaItem}>
            {project.clientName}
          </span>
        )}

        {/* Status badge */}
        <span
          style={{
            ...styles.statusBadge,
            ...getStatusColor(project.status),
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            console.log('Tag clicked:', project.status, project.id);
            // TODO: Navigate based on tag type
          }}
          title={t('projects.clickToFilterByStatus')}
        >
          {t(`projects.status.${getStatusKey(project.status)}`)}
        </span>

        {/* Progress bar */}
        <div style={styles.progressContainer}>
          <div
            style={{
              ...styles.progressBar,
              width: `${project.progress}%`,
              background: getProgressColor(project.progress, project.risk),
            }}
          />
          <span style={styles.progressText}>{project.progress}%</span>
        </div>

        {/* Risk indicator */}
        {project.risk && project.risk !== 'ok' && (
          <span style={{ ...styles.riskBadge, ...getRiskColor(project.risk) }}>
            {project.risk === 'warn' ? '⚠️' : '🔴'}
          </span>
        )}
      </div>
    );
  }, [project, t, getStatusKey]);

  return (
    <BaseRow
      entity={project}
      entityType="project"
      leftSlot={leftSlot}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={linkedCounts}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
      done={project.status === 'chiuso'}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// getStatusLabel is now handled by translation t(`projects.status.${key}`)

function getStatusColor(status: string): React.CSSProperties {
  const colors: Record<string, React.CSSProperties> = {
    'da-iniziare': { background: '#e5e7eb', color: '#6b7280' },
    'in-corso': { background: '#dbeafe', color: '#1d4ed8' },
    'a-rischio': { background: '#fef2f2', color: '#dc2626' },
    'in-pausa': { background: '#fef3c7', color: '#d97706' },
    'chiuso': { background: '#dcfce7', color: '#16a34a' },
  };
  return colors[status] || colors['da-iniziare'];
}

function getProjectAvatarColor(status: string): string {
  const colors: Record<string, string> = {
    'da-iniziare': '#9ca3af',
    'in-corso': '#3b82f6',
    'a-rischio': '#ef4444',
    'in-pausa': '#f59e0b',
    'chiuso': '#22c55e',
  };
  return colors[status] || '#9ca3af';
}

function getProgressColor(progress: number, risk?: string): string {
  if (risk === 'high') return '#ef4444';
  if (risk === 'warn') return '#f59e0b';
  if (progress >= 75) return '#22c55e';
  if (progress >= 50) return '#3b82f6';
  if (progress >= 25) return '#f59e0b';
  return '#ef4444';
}

function getRiskColor(risk: string): React.CSSProperties {
  if (risk === 'high') {
    return { color: '#dc2626' };
  }
  return { color: '#f59e0b' };
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
  } as React.CSSProperties,
  metaItem: {
    color: '#666',
  } as React.CSSProperties,
  statusBadge: {
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: 500,
  } as React.CSSProperties,
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: '80px',
  } as React.CSSProperties,
  progressBar: {
    height: '4px',
    borderRadius: '2px',
    minWidth: '60px',
    background: '#e5e7eb',
    position: 'relative',
  } as React.CSSProperties,
  progressText: {
    fontSize: '11px',
    color: '#666',
    minWidth: '30px',
  } as React.CSSProperties,
  riskBadge: {
    fontSize: '12px',
  } as React.CSSProperties,
};
